/**
 * "How Page Performance measures things" — the measures guide.
 *
 * Opened from the guide panel's button, the Page Detail header ("How these
 * are measured") and the ⓘ buttons, at the section that was asked for.
 * Copy adapted from the spec's guide (`#guide-load` … `#guide-status`) to
 * what this app records today; the sections for later phases (API and
 * database timings, where the time goes, baseline, detection rules) are
 * left out rather than promised.
 *
 * The limits are read from the server's `limits` (never typed here).
 */
import { useEffect, useRef } from 'react'
import { Modal } from '../ui/Modal'
import { formatLimitSeconds, formatPercent } from '../../lib/page-perf/view-model'

const TOC = [
  { group: 'How it works', items: [['how', '⚙', 'End to end'], ['load', 'P95', 'Page load time'], ['pct', 'AVG', 'Average and percentiles'], ['loads', '#', 'Page loads and users']] },
  { group: 'What the browser measured', items: [['ttfb', 'TTFB', 'Time To First Byte'], ['fcp', 'FCP', 'First Contentful Paint'], ['lcp', 'LCP', 'Largest Contentful Paint'], ['cls', 'CLS', 'Cumulative Layout Shift'], ['inp', 'INP', 'Interaction to Next Paint']] },
  { group: 'Rules', items: [['err', 'ERR', 'Error rate'], ['status', '●', 'Status colours']] },
]

function timeLimits(levels) {
  if (!levels) return 'Not sent by the server.'
  const w = formatLimitSeconds(levels.warning)
  return `Healthy ≤ ${w} · Warning > ${w} · Slow > ${formatLimitSeconds(levels.slow)} · Critical > ${formatLimitSeconds(levels.critical)}`
}

function rateLimits(levels) {
  if (!levels) return 'Not sent by the server.'
  const w = formatPercent(levels.warning)
  return `Healthy ≤ ${w} · Warning > ${w} · Slow > ${formatPercent(levels.slow)} · Critical > ${formatPercent(levels.critical)}`
}

function Pill({ children, tone = 'brand' }) {
  const cls =
    tone === 'brand'
      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border-[color:var(--color-accent-border)]'
      : tone === 'limit'
        ? 'bg-[color:var(--color-danger-bg)] text-[color:var(--color-danger-text)] border-[color:var(--color-danger-border)]'
        : 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]'
  return <span className={`ml-1.5 inline-flex rounded-full border px-2 py-0.5 align-middle text-[11px] font-medium ${cls}`}>{children}</span>
}

function Section({ id, title, pills, children }) {
  return (
    <section id={`guide-${id}`} data-guide={id} className="scroll-mt-2 border-b border-[color:var(--color-rule)] pb-4 last:border-b-0">
      <h3 className="m-0 font-display text-base font-semibold text-[color:var(--color-ink-strong)]">
        {title}
        {pills}
      </h3>
      <div className="mt-2 text-sm leading-relaxed text-[color:var(--color-ink)]">{children}</div>
    </section>
  )
}

function Rows({ rows }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[150px_1fr]">
      {rows.map(([dt, dd]) => (
        <div key={dt} className="contents">
          <dt className="font-semibold text-[color:var(--color-ink-muted)]">{dt}</dt>
          <dd className="m-0">{dd}</dd>
        </div>
      ))}
    </dl>
  )
}

export function MeasuresGuideModal({ open, onClose, section = 'how', limits }) {
  const bodyRef = useRef(null)

  useEffect(() => {
    if (!open) return undefined
    const id = requestAnimationFrame(() => {
      const el = document.getElementById(`guide-${section}`)
      el?.scrollIntoView({ block: 'start' })
    })
    return () => cancelAnimationFrame(id)
  }, [open, section])

  const go = (id) => document.getElementById(`guide-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' })

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      dismissOnBackdrop
      title="How Page Performance measures things"
      description="Every figure with its full name, what it means, how the app records it and where you see it."
    >
      <div ref={bodyRef} className="grid min-w-0 grid-cols-1 gap-5 md:grid-cols-[190px_1fr]">
        <nav aria-label="Terms" className="min-w-0 md:sticky md:top-0 md:self-start">
          {TOC.map((g) => (
            <div key={g.group} className="mb-3">
              <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wider text-[color:var(--color-ink-subtle)]">{g.group}</p>
              <div className="flex flex-wrap gap-1 md:flex-col md:gap-0.5">
                {g.items.map(([id, code, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => go(id)}
                    className={
                      'inline-flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-left text-[13px] hover:bg-[color:var(--color-card-hover)] ' +
                      (id === section ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)]' : 'text-[color:var(--color-ink)]')
                    }
                  >
                    <code className="min-w-[34px] text-[11px] text-[color:var(--color-ink-muted)]">{code}</code>
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="min-w-0 space-y-4">
          <Section id="how" title="How it works, end to end" pills={<Pill>nothing to install</Pill>}>
            <ol className="m-0 list-none space-y-2 p-0">
              <li><b>1 · Measured in the browser.</b> Every page a signed-in company member opens is timed by the app itself: when it was opened, when its API calls finished, when it settled and what the browser painted. Master Admins and impersonated sessions are never measured, and neither are the sign-in screens.</li>
              <li><b>2 · Sent in the background.</b> Measurements are batched and sent after the page has settled — a page is never slowed down, and a failed report changes nothing for the person using it.</li>
              <li><b>3 · Stored per company.</b> Every open is saved with the company the person was working in, so one company&rsquo;s figures never include another&rsquo;s.</li>
              <li><b>4 · Shown in the tabs.</b> Overview, Companies, Pages and Page Detail read those opens: raw opens for 30 days, then a daily history for 13 months.</li>
            </ol>
          </Section>

          <Section id="load" title="Page load time" pills={<><Pill>the status figure</Pill><Pill tone="limit">limit</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>How long a page took to open: from the moment the person navigated until the page <b>settled</b> — every API call it made had finished, nothing new started for 500 ms, and the next frame was painted. A full load (first open, refresh) starts at the browser&rsquo;s navigation start; an in-app navigation starts at the route change.</>],
                ['How it is measured', 'By the app’s own frontend: it watches the route and the app’s API calls to know when the page’s calls start and finish. An open is reported at the latest after 20 s and counts as a real slow open. An open the person left before it settled is counted on its own (“left before it finished”) and kept out of the averages and percentiles.'],
                ['Figure used', <><b>P95</b> over the period, per page — see percentiles.</>],
                ['Limits', timeLimits(limits?.load)],
                ['Where you see it', 'Overview tiles and chart · Pages table (Average, P50, P95, P99) · Page Detail tiles.'],
              ]}
            />
          </Section>

          <Section id="pct" title="Average and percentiles" pills={<Pill>Avg · P50 · P75 · P95 · P99</Pill>}>
            <p className="m-0 mb-2">Take every open of a page in the period and sort them from fastest to slowest. A percentile is the time at a given place in that line:</p>
            <Rows
              rows={[
                ['Average', 'All the times added up and divided by their number — one very slow open can pull it up.'],
                ['P50 (typical)', 'Half of the opens were faster than this — what most people feel.'],
                ['P75', 'Three of every four opens were faster. Used for LCP and INP, as the web-vitals standard does.'],
                ['P95', <><b>19 of every 20 opens were faster; only the slowest 1 in 20 took longer.</b> The status colour uses it: it shows the slow experience real people get without being thrown off by one freak open.</>],
                ['P99', '99 of 100 were faster — the very worst case.'],
                ['Thin data', 'Under about 20 opens, “typical” and “slowest” are simply those few opens; the screens say so.'],
              ]}
            />
          </Section>

          <Section id="loads" title="Page loads and users" pills={<Pill>what is counted</Pill>}>
            <Rows
              rows={[
                ['Page load', <>Every time a page is opened — a full browser load or an in-app navigation. Switching tabs inside one page (the URL&rsquo;s <code>?tab=</code>) is not a new load; a page is keyed by its path only, with ids replaced by <code>:id</code>.</>],
                ['Full load vs in-app', <><b>Full loads</b> (first open, refresh) also carry the browser figures below — TTFB, FCP, LCP. <b>In-app navigations</b> carry the load time and the API calls.</>],
                ['Users', 'Distinct people who opened pages. The person is hashed per day on the server (their id + the day + a secret), so nobody is identified and the same person counts once per day. Over several days the figure is people per day, added up.'],
                ['Never recorded', 'Query strings, form values, passwords, tokens, file contents, page titles, the person’s id, name or email address, or their IP address.'],
              ]}
            />
          </Section>

          <Section id="ttfb" title="TTFB" pills={<><Pill>Time To First Byte</Pill><Pill tone="plain">full loads only</Pill><Pill tone="limit">limit</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>The time between the browser asking for the page and receiving the <b>first byte</b> of the reply — the network round trip plus the server&rsquo;s time to start answering.</>],
                ['How it is measured', <>Navigation Timing: <code>responseStart − requestStart</code>. Only on full loads — an in-app navigation makes no document request, so it has no TTFB.</>],
                ['Limits', `${timeLimits(limits?.ttfb)} — P95 of full loads.`],
                ['Where you see it', 'Pages table (TTFB) · Page Detail tile “TTFB (full loads)” · it feeds the page’s status.'],
              ]}
            />
          </Section>

          <Section id="fcp" title="FCP" pills={<><Pill>First Contentful Paint</Pill><Pill tone="plain">full loads only</Pill><Pill tone="plain">for context</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>When the browser painted the <b>first piece of content</b> — any text or image — the first sign of life after a blank screen.</>],
                ['How it is measured', <>Paint Timing: the <code>first-contentful-paint</code> entry.</>],
                ['Good to know', 'The web-vitals guide calls ≤ 1.8 s good and > 3 s poor. It has no limit here and colours nothing.'],
                ['Where you see it', 'Page Detail tile “FCP (full loads)”.'],
              ]}
            />
          </Section>

          <Section id="lcp" title="LCP" pills={<><Pill>Largest Contentful Paint</Pill><Pill tone="plain">full loads only</Pill><Pill tone="limit">limit</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>When the <b>largest visible element</b> — usually the main table, chart or heading — finished painting: the moment the page &ldquo;looks loaded&rdquo;.</>],
                ['How it is measured', <>Largest Contentful Paint entries; the last one before the page settled counts.</>],
                ['Figure used', <><b>P75</b> of full loads, as the web-vitals standard does.</>],
                ['Limits', timeLimits(limits?.lcp)],
                ['Where you see it', 'Pages table (LCP) · Page Detail tile “LCP P75 (full loads)” · it feeds the page’s status.'],
              ]}
            />
          </Section>

          <Section id="cls" title="CLS" pills={<><Pill>Cumulative Layout Shift</Pill><Pill tone="plain">for context</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>How much the visible content <b>jumped around</b> while the page was open — a table pushing everything down when its data arrives, a button moving as you click it. A score, not a time: 0 means nothing moved.</>],
                ['How it is measured', 'The sum of every unexpected layout shift (shifts right after the person’s own input are ignored), once per page visit.'],
                ['Good to know', 'good ≤ 0.1 · needs improvement ≤ 0.25 · poor > 0.25'],
                ['Where you see it', 'Page Detail tile “CLS”.'],
              ]}
            />
          </Section>

          <Section id="inp" title="INP" pills={<><Pill>Interaction to Next Paint</Pill><Pill tone="plain">for context</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>How quickly the page <b>reacts</b> to a click, tap or key press: from the interaction until the next frame is drawn. A high INP is a page that feels stuck after you click.</>],
                ['How it is measured', 'Event Timing: the slowest interaction of the page visit; Page Detail shows the P75 across opens. A visit with no clicks or key presses has no INP.'],
                ['Good to know', 'good ≤ 200 ms · needs improvement ≤ 500 ms · poor > 500 ms'],
                ['Where you see it', 'Page Detail tile “INP P75”.'],
              ]}
            />
          </Section>

          <Section id="err" title="Error rate" pills={<><Pill>loads with a failed request</Pill><Pill tone="limit">limit</Pill></>}>
            <Rows
              rows={[
                ['What it is', <>The share of page opens in which <b>at least one API call failed</b>: a server error (5xx), a timeout (408), too many requests (429) or no reply — a permission or not-found reply (403, 404 …) does not count, as the server answered correctly.</>],
                ['Limits', rateLimits(limits?.errors)],
                ['Where you see it', 'Pages and Companies tables (Errors) · Overview tile · Page Detail tile · it feeds the page’s status.'],
              ]}
            />
          </Section>

          <Section id="status" title="Status colours" pills={<Pill>Healthy · Warning · Slow · Critical</Pill>}>
            <Rows
              rows={[
                ['How a page gets its colour', <>The <b>worst</b> of: page load time P95, TTFB P95, LCP P75 and the error rate, each against its limits. A company&rsquo;s colour is its worst page; the Overview&rsquo;s health bar counts pages by colour.</>],
                ['Thin data', 'A page with very few opens is still coloured, but Page Detail warns that the figures are thin below about 20 opens.'],
                ['Units', 'Limits are written in seconds and percent; the figures are measured in milliseconds and fractions. Every comparison converts first.'],
              ]}
            />
          </Section>
        </div>
      </div>
    </Modal>
  )
}
